// Procedural materials (presentation-2 contract section 4): gradients over a tinted base plus an
// inline SVG noise layer generated here. Data URIs are images, which the host's CSP allows.

import type { Material } from '../presentation/types.ts';

const DEFAULT_TINT: Record<Material, string> = {
  felt: '#1f4a40', wood: '#8a5a32', parchment: '#eadcbc', slate: '#3a4048', linen: '#e9e2d0', paper: '#f6f2e8', night: '#1a1f33',
};

/** Hex color mixed toward black (negative) or white (positive) by `amount` in -1..1. */
export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1, 7), 16);
  const ch = [n >> 16, (n >> 8) & 255, n & 255].map(v => Math.round(amount < 0 ? v * (1 + amount) : v + (255 - v) * amount));
  return '#' + ch.map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
}

/** Relative luminance, for choosing ink over a material. */
export function luminance(hex: string): number {
  const ch = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

function noise(frequency: number, opacity: number, stretch = 1): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='${frequency} ${frequency * stretch}' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 ${opacity} 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>`;
  return `url("data:image/svg+xml;utf8,${svg.replace(/"/g, "'").replace(/</g, '%3C').replace(/>/g, '%3E')}")`;
}

/** CSS `background` for a material, built from its tint. */
export function materialBackground(material: Material, tint?: string): string {
  const t = tint ?? DEFAULT_TINT[material];
  switch (material) {
    case 'felt':
      return `${noise(0.9, 0.22)}, radial-gradient(ellipse at 50% 40%, ${t} 0%, ${shade(t, -0.25)} 60%, ${shade(t, -0.5)} 100%)`;
    case 'wood':
      return `${noise(0.012, 0.28, 18)}, repeating-linear-gradient(92deg, ${shade(t, -0.12)} 0 3px, transparent 3px 11px, ${shade(t, 0.06)} 11px 13px, transparent 13px 24px), linear-gradient(180deg, ${shade(t, 0.08)}, ${shade(t, -0.18)})`;
    case 'parchment':
      return `${noise(0.6, 0.12)}, radial-gradient(ellipse at 50% 45%, ${shade(t, 0.18)} 0%, ${t} 55%, ${shade(t, -0.16)} 100%)`;
    case 'slate':
      return `${noise(0.5, 0.25)}, linear-gradient(160deg, ${shade(t, 0.1)}, ${shade(t, -0.2)})`;
    case 'linen':
      return `${noise(0.8, 0.1)}, repeating-linear-gradient(0deg, ${shade(t, -0.04)} 0 1px, transparent 1px 3px), repeating-linear-gradient(90deg, ${shade(t, -0.04)} 0 1px, transparent 1px 3px), ${t}`;
    case 'paper':
      return `${noise(0.7, 0.06)}, ${t}`;
    case 'night':
      return `${noise(0.9, 0.18)}, radial-gradient(ellipse at 50% 30%, ${shade(t, 0.15)} 0%, ${t} 60%, ${shade(t, -0.4)} 100%)`;
  }
}

/** Ink that reads on a material: light on dark materials, dark on light ones. */
export function materialInk(material: Material, tint?: string): { ink: string; muted: string } {
  const t = tint ?? DEFAULT_TINT[material];
  return luminance(t) < 0.2 ? { ink: '#f4efe3', muted: '#c9c1ad' } : { ink: '#2b2416', muted: '#5f5544' };
}

export function materialTint(material: Material, tint?: string): string {
  return tint ?? DEFAULT_TINT[material];
}
